import { uiText } from '../i18n/uiText';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { templatesApi } from '../api';
import { Plus, FileText, Edit2, Trash2, Send, Search, CalendarDays, UserRound } from 'lucide-react';
import PageHeader from '../components/common/PageHeader';
import { useConfirm } from '../store/ConfirmContext';
import { formatAuditType } from '../utils/formatters';

const formatDateDMY = (dt) => {
  if (!dt) return '—';
  try {
    const d = new Date(dt);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`;
  } catch { return dt; }
};

const STATUS = {
  draft:     { label: uiText("Черновик"), cls: 'badge-gray' },
  published: { label: uiText("Опубликован"), cls: 'badge-red' },
};

export default function TemplatesPage() {
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await templatesApi.list();
      setTemplates(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!await confirm(uiText("Удалить шаблон? Это действие нельзя отменить."), { title: uiText("Удаление шаблона"), confirmLabel: uiText("Удалить") })) return;
    await templatesApi.delete(id);
    load();
  };

  const handlePublish = async (id, e) => {
    e.stopPropagation();
    await templatesApi.publish(id);
    load();
  };

  const filtered = templates.filter(t => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.name.toLowerCase().includes(q) || t.client?.toLowerCase().includes(q) || t.audit_type?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts = {
    all: templates.length,
    draft: templates.filter(t => t.status === 'draft').length,
    published: templates.filter(t => t.status === 'published').length,
  };

  const formatDate = formatDateDMY;

  return (
    <div>
      <PageHeader
        icon={FileText}
        title={uiText("Шаблоны чек-листов")}
        subtitle={uiText("Чек-листы для различных типов аудитов. Опубликованные шаблоны используются при создании новых аудитов.")}
        right={
          <button type="button" className="btn btn-primary" onClick={() => navigate('/templates/new')}>
            <Plus size={15} />{uiText(" Создать шаблон")}</button>
        }
      />

      {/* Filter bar */}
      <div className="filter-bar">
        <div className="search-bar" style={{ flex: '1', maxWidth: '400px' }}>
          <input placeholder={uiText("Поиск по названию, клиенту или типу аудита...")} value={search} onChange={e => setSearch(e.target.value)} />
          <span className="search-bar-icon" aria-hidden="true"><Search /></span>
        </div>
        <div className="ap-control-options">
          {[['all', uiText("Все ({v0})", {v0: (counts.all)})], ['draft', uiText("Черновики ({v0})", {v0: (counts.draft)})], ['published', uiText("Опубликованные ({v0})", {v0: (counts.published)})]].map(([val, label]) => (
            <button type="button" key={val} className="ap-button ap-button--secondary ap-control-action" aria-pressed={statusFilter === val} onClick={() => setStatusFilter(val)}>{label}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{uiText("Название шаблона")}</th>
                  <th>{uiText("Клиент")}</th>
                  <th>{uiText("Тип аудита")}</th>
                  <th>{uiText("Язык")}</th>
                  <th>{uiText("Статус")}</th>
                  <th>{uiText("Версия")}</th>
                  <th>{uiText("Последнее изменение")}</th>
                  <th>{uiText("Автор")}</th>
                  <th>{uiText("Действия")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#888' }}>{uiText("Шаблоны не найдены")}</td></tr>
                ) : filtered.map(t => {
                  const s = STATUS[t.status] || STATUS.draft;
                  return (
                    <tr key={t.id} onClick={() => navigate(`/templates/${t.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FileText size={14} color="#888" />
                          <span style={{ fontWeight: '500' }}>{t.name}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: '12px' }}>{t.client || '—'}</td>
                      <td style={{ fontSize: '12px', maxWidth: '200px' }}>
                        <span title={formatAuditType(t.audit_type)}>{formatAuditType(t.audit_type)}</span>
                      </td>
                      <td><span className="badge badge-gray">{t.language}</span></td>
                      <td><span className={`badge ${s.cls}`}>{s.label}</span></td>
                      <td style={{ fontSize: '12px', fontWeight: '500' }}>{t.version}</td>
                      <td><span className="workspace-inline-meta"><CalendarDays size={13} /> {formatDate(t.updated_at)}</span></td>
                      <td><span className="workspace-inline-meta"><UserRound size={13} /> {t.author_name}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px' }} onClick={e => e.stopPropagation()}>
                          <button type="button" className="btn btn-ghost btn-sm" title={uiText("Редактировать")} onClick={() => navigate(`/templates/${t.id}`)}>
                            <Edit2 size={13} />
                          </button>
                          {t.status === 'draft' && (
                            <button type="button" className="btn btn-ghost btn-sm" title={uiText("Опубликовать")} onClick={e => handlePublish(t.id, e)}>
                              <Send size={13} />
                            </button>
                          )}
                          <button type="button" className="btn btn-ghost btn-sm" title={uiText("Удалить")} onClick={e => handleDelete(t.id, e)} style={{ color: '#C0392B' }}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
